import { useEffect, useMemo, useState } from "react";
import { formatEther, parseEther, parseGwei, type Address } from "viem";
import {
  useAccount,
  usePublicClient,
  useReadContract,
  useWaitForTransactionReceipt,
  useWriteContract,
} from "wagmi";

import { factoryAbi } from "../contracts/factoryAbi";
import { factoryAddress } from "../contracts/factoryAddress";

type AdminMarketItem = {
  marketId: bigint;
  question: string;
  resolutionSource: string;
  deadline: bigint;
  creator: string;
  resolved: boolean;
  outcome: boolean;
  yesPool: bigint;
  noPool: bigint;
  totalYesShares: bigint;
  totalNoShares: bigint;
  virtualLiquidity: bigint;
  resolutionEvidenceUrl: string;
  resolutionNotes: string;
  resolver: string;
  resolvedAt: bigint;
};

type CreateMode = "STANDARD" | "SEEDED";

type ResolutionDraft = {
  outcome: boolean;
  evidenceUrl: string;
  notes: string;
};

const AMOY_MAX_PRIORITY_FEE_PER_GAS = parseGwei("30");
const AMOY_MAX_FEE_PER_GAS = parseGwei("60");

function formatPol(value: bigint, decimals = 4) {
  try {
    const num = Number(formatEther(value));
    if (!Number.isFinite(num)) return "0";
    return num.toFixed(decimals);
  } catch {
    return "0";
  }
}

function formatAddress(value: string, left = 6, right = 4) {
  if (!value) return "—";
  if (value.length <= left + right) return value;
  return `${value.slice(0, left)}...${value.slice(-right)}`;
}

function formatDate(timestamp: bigint) {
  try {
    const ms = Number(timestamp) * 1000;
    if (!Number.isFinite(ms) || ms <= 0) return "—";
    return new Date(ms).toLocaleString();
  } catch {
    return "—";
  }
}

function safeBigInt(value: unknown): bigint {
  if (typeof value === "bigint") return value;
  if (typeof value === "number") return BigInt(value);
  if (typeof value === "string") {
    try {
      return BigInt(value);
    } catch {
      return 0n;
    }
  }
  return 0n;
}

function safeBoolean(value: unknown): boolean {
  return Boolean(value);
}

function safeString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function normalizeMarket(raw: unknown): AdminMarketItem {
  if (Array.isArray(raw)) {
    return {
      marketId: safeBigInt(raw[0]),
      question: safeString(raw[1]),
      resolutionSource: safeString(raw[2]),
      deadline: safeBigInt(raw[3]),
      creator: safeString(raw[4]),
      resolved: safeBoolean(raw[5]),
      outcome: safeBoolean(raw[6]),
      yesPool: safeBigInt(raw[7]),
      noPool: safeBigInt(raw[8]),
      totalYesShares: safeBigInt(raw[9]),
      totalNoShares: safeBigInt(raw[10]),
      virtualLiquidity: safeBigInt(raw[11]),
      resolutionEvidenceUrl: safeString(raw[12]),
      resolutionNotes: safeString(raw[13]),
      resolver: safeString(raw[14]),
      resolvedAt: safeBigInt(raw[15]),
    };
  }

  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    return {
      marketId: safeBigInt(obj.id),
      question: safeString(obj.question),
      resolutionSource: safeString(obj.resolutionSource),
      deadline: safeBigInt(obj.deadline),
      creator: safeString(obj.creator),
      resolved: safeBoolean(obj.resolved),
      outcome: safeBoolean(obj.outcome),
      yesPool: safeBigInt(obj.yesPool),
      noPool: safeBigInt(obj.noPool),
      totalYesShares: safeBigInt(obj.totalYesShares),
      totalNoShares: safeBigInt(obj.totalNoShares),
      virtualLiquidity: safeBigInt(obj.virtualLiquidity),
      resolutionEvidenceUrl: safeString(obj.resolutionEvidenceUrl),
      resolutionNotes: safeString(obj.resolutionNotes),
      resolver: safeString(obj.resolver),
      resolvedAt: safeBigInt(obj.resolvedAt),
    };
  }

  return {
    marketId: 0n,
    question: "",
    resolutionSource: "",
    deadline: 0n,
    creator: "",
    resolved: false,
    outcome: false,
    yesPool: 0n,
    noPool: 0n,
    totalYesShares: 0n,
    totalNoShares: 0n,
    virtualLiquidity: 0n,
    resolutionEvidenceUrl: "",
    resolutionNotes: "",
    resolver: "",
    resolvedAt: 0n,
  };
}

function pad2(value: number) {
  return String(value).padStart(2, "0");
}

function getDefaultCloseDateTime() {
  const now = new Date();
  const future = new Date(now.getTime() + 60 * 60 * 1000);
  future.setSeconds(0, 0);

  const date = `${future.getFullYear()}-${pad2(future.getMonth() + 1)}-${pad2(
    future.getDate()
  )}`;
  const time = `${pad2(future.getHours())}:${pad2(future.getMinutes())}`;

  return { date, time };
}

function combineLocalDateTime(date: string, time: string) {
  if (!date || !time) return null;
  const combined = new Date(`${date}T${time}:00`);
  if (Number.isNaN(combined.getTime())) return null;
  return combined;
}

export default function AdminPage() {
  const { isConnected } = useAccount();
  const publicClient = usePublicClient();
  const contractAddress = factoryAddress as Address;

  const defaultClose = useMemo(() => getDefaultCloseDateTime(), []);

  const [createMode, setCreateMode] = useState<CreateMode>("SEEDED");
  const [question, setQuestion] = useState("");
  const [resolutionSource, setResolutionSource] = useState("Orion admin");
  const [closeDate, setCloseDate] = useState(defaultClose.date);
  const [closeTime, setCloseTime] = useState(defaultClose.time);
  const [useCustomVirtualLiquidity, setUseCustomVirtualLiquidity] = useState(false);
  const [virtualLiquidity, setVirtualLiquidity] = useState("0.25");
  const [yesSeedAmount, setYesSeedAmount] = useState("0.25");
  const [noSeedAmount, setNoSeedAmount] = useState("0.25");
  const [markets, setMarkets] = useState<AdminMarketItem[]>([]);
  const [isLoadingMarkets, setIsLoadingMarkets] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [resolutionDrafts, setResolutionDrafts] = useState<Record<string, ResolutionDraft>>({});

  const { data: ownerRaw } = useReadContract({
    address: contractAddress,
    abi: factoryAbi,
    functionName: "owner",
  });

  const { data: treasuryRaw } = useReadContract({
    address: contractAddress,
    abi: factoryAbi,
    functionName: "treasury",
  });

  const { data: feeBpsRaw } = useReadContract({
    address: contractAddress,
    abi: factoryAbi,
    functionName: "feeBps",
  });

  const { data: nextMarketIdRaw } = useReadContract({
    address: contractAddress,
    abi: factoryAbi,
    functionName: "nextMarketId",
  });

  const {
    writeContract,
    data: txHash,
    isPending: isWritePending,
    error: writeError,
    reset,
  } = useWriteContract();

  const { isLoading: isConfirming, isSuccess: isConfirmed } =
    useWaitForTransactionReceipt({
      hash: txHash,
      query: { enabled: !!txHash },
    });

  useEffect(() => {
    if (!publicClient) return;
    let cancelled = false;

    async function loadMarkets() {
      try {
        setIsLoadingMarkets(true);
        setErrorMessage("");

        const idsRaw = await publicClient.readContract({
          address: contractAddress,
          abi: factoryAbi,
          functionName: "getActiveMarketIds",
        });

        const ids = Array.isArray(idsRaw) ? idsRaw.map((v) => safeBigInt(v)) : [];
        const loadedMarkets: AdminMarketItem[] = [];

        for (const marketId of ids) {
          const marketRaw = await publicClient.readContract({
            address: contractAddress,
            abi: factoryAbi,
            functionName: "getMarket",
            args: [marketId],
          });
          loadedMarkets.push(normalizeMarket(marketRaw));
        }

        loadedMarkets.sort((a, b) => Number(a.marketId - b.marketId));

        if (!cancelled) {
          setMarkets(loadedMarkets);

          setResolutionDrafts((prev) => {
            const next = { ...prev };
            for (const market of loadedMarkets) {
              const key = market.marketId.toString();
              if (!next[key]) {
                next[key] = {
                  outcome: true,
                  evidenceUrl: "",
                  notes: "",
                };
              }
            }
            return next;
          });
        }
      } catch (error) {
        console.error("Failed to load admin markets:", error);
        if (!cancelled) setErrorMessage("Failed to load admin markets.");
      } finally {
        if (!cancelled) setIsLoadingMarkets(false);
      }
    }

    loadMarkets();
    return () => {
      cancelled = true;
    };
  }, [publicClient, contractAddress, refreshNonce]);

  useEffect(() => {
    if (isConfirmed) setRefreshNonce((prev) => prev + 1);
  }, [isConfirmed]);

  const pendingLabel = useMemo(() => {
    if (isWritePending) return "Please confirm in wallet...";
    if (isConfirming) return "Waiting for blockchain confirmation...";
    if (isConfirmed) return "Transaction confirmed.";
    if (writeError) return writeError.message || "Transaction failed.";
    return "";
  }, [isWritePending, isConfirming, isConfirmed, writeError]);

  const closeAt = useMemo(() => combineLocalDateTime(closeDate, closeTime), [closeDate, closeTime]);

  const durationMinutesPreview = useMemo(() => {
    if (!closeAt) return null;
    const diffMs = closeAt.getTime() - Date.now();
    if (diffMs <= 0) return 0;
    return Math.ceil(diffMs / 60000);
  }, [closeAt]);

  function clearCreateForm() {
    const nextDefault = getDefaultCloseDateTime();
    setQuestion("");
    setResolutionSource("Orion admin");
    setCloseDate(nextDefault.date);
    setCloseTime(nextDefault.time);
    setUseCustomVirtualLiquidity(false);
    setVirtualLiquidity("0.25");
    setYesSeedAmount("0.25");
    setNoSeedAmount("0.25");
  }

  function setResolutionDraft(
    marketId: bigint,
    patch: Partial<ResolutionDraft>
  ) {
    const key = marketId.toString();
    setResolutionDrafts((prev) => ({
      ...prev,
      [key]: {
        outcome: prev[key]?.outcome ?? true,
        evidenceUrl: prev[key]?.evidenceUrl ?? "",
        notes: prev[key]?.notes ?? "",
        ...patch,
      },
    }));
  }

  async function handleCreateMarket(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    reset();

    const trimmedQuestion = question.trim();
    const trimmedSource = resolutionSource.trim();

    if (!trimmedQuestion) {
      setErrorMessage("Please enter a market question.");
      return;
    }

    if (!trimmedSource) {
      setErrorMessage("Please enter a resolution source.");
      return;
    }

    if (!closeDate || !closeTime || !closeAt) {
      setErrorMessage("Please enter a valid close date and time.");
      return;
    }

    const duration = durationMinutesPreview ?? 0;

    if (!Number.isFinite(duration) || duration <= 0) {
      setErrorMessage("Please choose a future close date and time.");
      return;
    }

    setErrorMessage("");

    try {
      if (createMode === "STANDARD") {
        if (useCustomVirtualLiquidity) {
          const parsedVirtualLiquidity = parseEther(virtualLiquidity || "0");
          if (parsedVirtualLiquidity <= 0n) {
            setErrorMessage("Please enter a valid custom virtual liquidity.");
            return;
          }

          writeContract({
            address: contractAddress,
            abi: factoryAbi,
            functionName: "createMarketWithLiquidity",
            args: [trimmedQuestion, trimmedSource, BigInt(duration), parsedVirtualLiquidity],
            maxPriorityFeePerGas: AMOY_MAX_PRIORITY_FEE_PER_GAS,
            maxFeePerGas: AMOY_MAX_FEE_PER_GAS,
          } as any);
        } else {
          writeContract({
            address: contractAddress,
            abi: factoryAbi,
            functionName: "createMarket",
            args: [trimmedQuestion, trimmedSource, BigInt(duration)],
            maxPriorityFeePerGas: AMOY_MAX_PRIORITY_FEE_PER_GAS,
            maxFeePerGas: AMOY_MAX_FEE_PER_GAS,
          } as any);
        }

        clearCreateForm();
        return;
      }

      const parsedYesSeed = parseEther(yesSeedAmount || "0");
      const parsedNoSeed = parseEther(noSeedAmount || "0");

      if (parsedYesSeed === 0n && parsedNoSeed === 0n) {
        setErrorMessage("Please seed at least one side.");
        return;
      }

      const totalSeed = parsedYesSeed + parsedNoSeed;

      if (useCustomVirtualLiquidity) {
        const parsedVirtualLiquidity = parseEther(virtualLiquidity || "0");
        if (parsedVirtualLiquidity <= 0n) {
          setErrorMessage("Please enter a valid custom virtual liquidity.");
          return;
        }

        writeContract({
          address: contractAddress,
          abi: factoryAbi,
          functionName: "createSeededMarketWithLiquidity",
          args: [
            trimmedQuestion,
            trimmedSource,
            BigInt(duration),
            parsedVirtualLiquidity,
            parsedYesSeed,
            parsedNoSeed,
          ],
          value: totalSeed,
          maxPriorityFeePerGas: AMOY_MAX_PRIORITY_FEE_PER_GAS,
          maxFeePerGas: AMOY_MAX_FEE_PER_GAS,
        } as any);
      } else {
        writeContract({
          address: contractAddress,
          abi: factoryAbi,
          functionName: "createSeededMarket",
          args: [trimmedQuestion, trimmedSource, BigInt(duration), parsedYesSeed, parsedNoSeed],
          value: totalSeed,
          maxPriorityFeePerGas: AMOY_MAX_PRIORITY_FEE_PER_GAS,
          maxFeePerGas: AMOY_MAX_FEE_PER_GAS,
        } as any);
      }

      clearCreateForm();
    } catch (error) {
      console.error(error);
      setErrorMessage("Invalid numeric input.");
    }
  }

  function handleResolveMarket(marketId: bigint) {
    reset();

    const draft = resolutionDrafts[marketId.toString()] ?? {
      outcome: true,
      evidenceUrl: "",
      notes: "",
    };

    const trimmedEvidenceUrl = draft.evidenceUrl.trim();

    if (!trimmedEvidenceUrl) {
      setErrorMessage("Please add a resolution evidence URL before resolving.");
      return;
    }

    setErrorMessage("");

    writeContract({
      address: contractAddress,
      abi: factoryAbi,
      functionName: "resolveMarket",
      args: [marketId, draft.outcome, trimmedEvidenceUrl, draft.notes.trim()],
      maxPriorityFeePerGas: AMOY_MAX_PRIORITY_FEE_PER_GAS,
      maxFeePerGas: AMOY_MAX_FEE_PER_GAS,
    } as any);
  }

  const nowMs = Date.now();

  const totalSeedPreview = useMemo(() => {
    try {
      if (createMode !== "SEEDED") return 0n;
      return parseEther(yesSeedAmount || "0") + parseEther(noSeedAmount || "0");
    } catch {
      return 0n;
    }
  }, [createMode, yesSeedAmount, noSeedAmount]);

  const stats = useMemo(() => {
    const totalLiquidity = markets.reduce((sum, m) => sum + m.yesPool + m.noPool, 0n);
    const openMarkets = markets.filter(
      (m) => !m.resolved && Number(m.deadline) * 1000 > nowMs
    ).length;
    const resolvedMarkets = markets.filter((m) => m.resolved).length;

    return {
      totalMarkets: markets.length,
      openMarkets,
      resolvedMarkets,
      totalLiquidity,
    };
  }, [markets, nowMs]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight">Admin Market Resolution</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Create markets, seed liquidity, and resolve them with clear evidence for beta users.
        </p>
      </div>

      <div className="mb-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl border bg-card p-5 shadow-sm">
          <div className="text-sm text-muted-foreground">Treasury wallet</div>
          <div className="mt-2 text-lg font-semibold">{formatAddress(String(treasuryRaw ?? ""))}</div>
          <div className="mt-2 break-all text-xs text-muted-foreground">{String(treasuryRaw ?? "—")}</div>
        </div>

        <div className="rounded-2xl border bg-card p-5 shadow-sm">
          <div className="text-sm text-muted-foreground">Owner wallet</div>
          <div className="mt-2 text-lg font-semibold">{formatAddress(String(ownerRaw ?? ""))}</div>
          <div className="mt-2 break-all text-xs text-muted-foreground">{String(ownerRaw ?? "—")}</div>
        </div>

        <div className="rounded-2xl border bg-card p-5 shadow-sm">
          <div className="text-sm text-muted-foreground">Protocol fee</div>
          <div className="mt-2 text-3xl font-semibold">
            {feeBpsRaw != null ? (Number(feeBpsRaw) / 100).toFixed(2) : "0.00"}%
          </div>
          <div className="mt-2 text-xs text-muted-foreground">
            {feeBpsRaw != null ? `${String(feeBpsRaw)} bps` : "—"}
          </div>
        </div>

        <div className="rounded-2xl border bg-card p-5 shadow-sm">
          <div className="text-sm text-muted-foreground">Next market ID</div>
          <div className="mt-2 text-3xl font-semibold">
            {nextMarketIdRaw != null ? String(nextMarketIdRaw) : "—"}
          </div>
          <div className="mt-2 text-xs text-muted-foreground">
            Contract: {formatAddress(factoryAddress)}
          </div>
        </div>
      </div>

      <div className="mb-8 grid gap-4 md:grid-cols-4">
        <div className="rounded-2xl border bg-card p-5 shadow-sm">
          <div className="text-sm text-muted-foreground">Total markets</div>
          <div className="mt-2 text-3xl font-semibold">{stats.totalMarkets}</div>
        </div>
        <div className="rounded-2xl border bg-card p-5 shadow-sm">
          <div className="text-sm text-muted-foreground">Open markets</div>
          <div className="mt-2 text-3xl font-semibold">{stats.openMarkets}</div>
        </div>
        <div className="rounded-2xl border bg-card p-5 shadow-sm">
          <div className="text-sm text-muted-foreground">Resolved markets</div>
          <div className="mt-2 text-3xl font-semibold">{stats.resolvedMarkets}</div>
        </div>
        <div className="rounded-2xl border bg-card p-5 shadow-sm">
          <div className="text-sm text-muted-foreground">Active liquidity</div>
          <div className="mt-2 text-3xl font-semibold">{formatPol(stats.totalLiquidity)} POL</div>
        </div>
      </div>

      {!isConnected && (
        <div className="mb-6 rounded-2xl border bg-card p-5 text-sm text-muted-foreground shadow-sm">
          Connect your wallet to use admin actions.
        </div>
      )}

      {(pendingLabel || errorMessage) && (
        <div className="mb-6 rounded-2xl border bg-card p-5 text-sm shadow-sm">
          {pendingLabel && <div className="text-muted-foreground">{pendingLabel}</div>}
          {errorMessage && <div className="mt-2 text-red-500">{errorMessage}</div>}
        </div>
      )}

      <div className="mb-8 rounded-2xl border bg-card p-6 shadow-sm">
        <div className="mb-5">
          <h2 className="text-xl font-semibold">Create Market</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Create low-liquidity beta markets with clear resolution sources and optional treasury seeding.
          </p>
        </div>

        <div className="mb-5 rounded-2xl bg-muted/40 p-1">
          <div className="grid grid-cols-2 gap-1">
            <button
              type="button"
              onClick={() => setCreateMode("SEEDED")}
              className={`rounded-2xl px-4 py-3 text-left transition ${
                createMode === "SEEDED"
                  ? "bg-foreground text-background shadow-sm"
                  : "bg-transparent text-foreground hover:bg-background/70"
              }`}
            >
              <div className="text-xs font-medium opacity-90">Recommended</div>
              <div className="mt-1 text-lg font-semibold">Seeded</div>
              <div className="mt-1 text-xs opacity-80">Better for early beta liquidity</div>
            </button>

            <button
              type="button"
              onClick={() => setCreateMode("STANDARD")}
              className={`rounded-2xl px-4 py-3 text-left transition ${
                createMode === "STANDARD"
                  ? "bg-foreground text-background shadow-sm"
                  : "bg-transparent text-foreground hover:bg-background/70"
              }`}
            >
              <div className="text-xs font-medium opacity-90">Alternative</div>
              <div className="mt-1 text-lg font-semibold">Standard</div>
              <div className="mt-1 text-xs opacity-80">No initial treasury seed</div>
            </button>
          </div>
        </div>

        <form onSubmit={handleCreateMarket} className="grid gap-4">
          <div>
            <label className="mb-2 block text-sm font-medium">Question</label>
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Will Cyprus GDP growth exceed 3% in 2026?"
              className="w-full rounded-xl border bg-background px-4 py-3 outline-none transition focus:border-primary"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">Resolution source</label>
            <input
              type="text"
              value={resolutionSource}
              onChange={(e) => setResolutionSource(e.target.value)}
              placeholder="Reuters / Eurostat / Official result / Orion admin"
              className="w-full rounded-xl border bg-background px-4 py-3 outline-none transition focus:border-primary"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {["Orion admin", "Reuters", "Eurostat", "Official result"].map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => setResolutionSource(preset)}
                className="rounded-full border px-3 py-1.5 text-sm text-muted-foreground transition hover:bg-muted/50"
              >
                {preset}
              </button>
            ))}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-xl border bg-background p-4">
              <label className="mb-3 block text-sm font-medium">Market closes</label>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-xs text-muted-foreground">Close date</label>
                  <input
                    type="date"
                    value={closeDate}
                    onChange={(e) => setCloseDate(e.target.value)}
                    className="w-full rounded-xl border bg-background px-4 py-3 outline-none transition focus:border-primary"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-xs text-muted-foreground">Close time</label>
                  <input
                    type="time"
                    value={closeTime}
                    onChange={(e) => setCloseTime(e.target.value)}
                    className="w-full rounded-xl border bg-background px-4 py-3 outline-none transition focus:border-primary"
                  />
                </div>
              </div>

              <div className="mt-3 text-sm text-muted-foreground">
                {closeAt ? (
                  <>
                    Closes at: <span className="font-medium text-foreground">{closeAt.toLocaleString()}</span>
                    <br />
                    Contract duration:{" "}
                    <span className="font-medium text-foreground">
                      {durationMinutesPreview ?? 0} minutes
                    </span>
                  </>
                ) : (
                  "Choose a valid future date and time."
                )}
              </div>
            </div>

            <div className="rounded-xl border bg-background p-4">
              <div className="mb-2 flex items-center justify-between">
                <label className="text-sm font-medium">Virtual liquidity</label>
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={useCustomVirtualLiquidity}
                    onChange={(e) => setUseCustomVirtualLiquidity(e.target.checked)}
                  />
                  Custom
                </label>
              </div>

              {useCustomVirtualLiquidity ? (
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={virtualLiquidity}
                  onChange={(e) => setVirtualLiquidity(e.target.value)}
                  className="w-full rounded-xl border bg-background px-4 py-3 outline-none transition focus:border-primary"
                />
              ) : (
                <div className="text-sm text-muted-foreground">
                  Default contract value will be used: 0.25 POL
                </div>
              )}
            </div>
          </div>

          {createMode === "SEEDED" && (
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <label className="mb-2 block text-sm font-medium">YES seed (POL)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={yesSeedAmount}
                  onChange={(e) => setYesSeedAmount(e.target.value)}
                  className="w-full rounded-xl border bg-background px-4 py-3 outline-none transition focus:border-primary"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium">NO seed (POL)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={noSeedAmount}
                  onChange={(e) => setNoSeedAmount(e.target.value)}
                  className="w-full rounded-xl border bg-background px-4 py-3 outline-none transition focus:border-primary"
                />
              </div>

              <div className="rounded-xl border bg-background p-4">
                <div className="text-sm text-muted-foreground">Total seed sent</div>
                <div className="mt-2 text-2xl font-semibold">{formatPol(totalSeedPreview)} POL</div>
                <div className="mt-2 text-xs text-muted-foreground">
                  This amount is sent with the creation transaction.
                </div>
              </div>
            </div>
          )}

          <div className="rounded-xl border bg-muted/20 p-4 text-sm text-muted-foreground">
            {createMode === "SEEDED"
              ? "Seeded markets launch with treasury-owned YES/NO shares, helping price discovery and smoother first trades."
              : "Standard markets launch with zero real pool liquidity and rely only on virtual liquidity until users trade."}
          </div>

          <div>
            <button
              type="submit"
              disabled={
                !isConnected ||
                isWritePending ||
                isConfirming ||
                !durationMinutesPreview ||
                durationMinutesPreview <= 0
              }
              className="rounded-xl bg-primary px-5 py-3 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isWritePending || isConfirming
                ? "Submitting..."
                : createMode === "SEEDED"
                ? "Create Seeded Market"
                : "Create Standard Market"}
            </button>
          </div>
        </form>
      </div>

      <div className="space-y-4">
        {isLoadingMarkets && (
          <div className="rounded-2xl border bg-card p-6 text-sm text-muted-foreground shadow-sm">
            Loading markets...
          </div>
        )}

        {!isLoadingMarkets && markets.length === 0 && (
          <div className="rounded-2xl border bg-card p-6 text-sm text-muted-foreground shadow-sm">
            No markets found.
          </div>
        )}

        {!isLoadingMarkets &&
          markets.map((market) => {
            const deadlinePassed = Number(market.deadline) * 1000 <= nowMs;
            const totalPool = market.yesPool + market.noPool;
            const draft = resolutionDrafts[market.marketId.toString()] ?? {
              outcome: true,
              evidenceUrl: "",
              notes: "",
            };

            return (
              <div
                key={market.marketId.toString()}
                className="rounded-2xl border bg-card p-6 shadow-sm"
              >
                <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                  <div className="max-w-3xl">
                    <div className="text-sm text-muted-foreground">
                      Market #{market.marketId.toString()}
                    </div>
                    <h3 className="mt-2 text-2xl font-semibold leading-tight">
                      {market.question}
                    </h3>

                    <div className="mt-3 rounded-xl border bg-background/60 p-4 text-sm">
                      <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground">
                        Resolution source
                      </div>
                      <div className="mt-2 font-medium text-foreground">
                        {market.resolutionSource || "—"}
                      </div>
                    </div>

                    <div className="mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                      <div>Deadline: {formatDate(market.deadline)}</div>
                      <div>YES Pool: {formatPol(market.yesPool)} POL</div>
                      <div>NO Pool: {formatPol(market.noPool)} POL</div>
                      <div>Total Pool: {formatPol(totalPool)} POL</div>
                      <div>YES Shares: {formatPol(market.totalYesShares)}</div>
                      <div>NO Shares: {formatPol(market.totalNoShares)}</div>
                      <div>Virtual Liquidity: {formatPol(market.virtualLiquidity)} POL</div>
                      <div>Creator: {formatAddress(market.creator)}</div>
                    </div>

                    {market.resolved && (
                      <>
                        <div className="mt-4 grid gap-3 sm:grid-cols-2">
                          <div className="rounded-xl border bg-background p-4 text-sm">
                            <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground">
                              Evidence
                            </div>
                            <div className="mt-2 font-medium break-all">
                              {market.resolutionEvidenceUrl ? (
                                <a
                                  href={market.resolutionEvidenceUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="underline"
                                >
                                  {market.resolutionEvidenceUrl}
                                </a>
                              ) : (
                                "—"
                              )}
                            </div>
                          </div>

                          <div className="rounded-xl border bg-background p-4 text-sm">
                            <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground">
                              Resolved metadata
                            </div>
                            <div className="mt-2 space-y-1 text-muted-foreground">
                              <div>Resolver: {formatAddress(market.resolver)}</div>
                              <div>Resolved at: {formatDate(market.resolvedAt)}</div>
                            </div>
                          </div>
                        </div>

                        {market.resolutionNotes && (
                          <div className="mt-3 rounded-xl border bg-background p-4 text-sm">
                            <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground">
                              Resolution notes
                            </div>
                            <div className="mt-2 whitespace-pre-wrap font-medium text-foreground">
                              {market.resolutionNotes}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <div className="min-w-[320px]">
                    {market.resolved ? (
                      <div className="space-y-3">
                        <div className="inline-flex rounded-full border border-emerald-500/20 bg-emerald-500/10 px-4 py-2 text-sm font-medium text-emerald-600">
                          Resolved: {market.outcome ? "YES" : "NO"}
                        </div>
                      </div>
                    ) : !deadlinePassed ? (
                      <div className="inline-flex rounded-full border border-amber-500/20 bg-amber-500/10 px-4 py-2 text-sm font-medium text-amber-600">
                        Waiting for deadline
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="text-sm font-medium">Resolve market</div>

                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              setResolutionDraft(market.marketId, { outcome: true })
                            }
                            className={`rounded-xl border px-4 py-2 text-sm ${
                              draft.outcome
                                ? "border-emerald-500 bg-emerald-500/10 text-emerald-600"
                                : "text-muted-foreground"
                            }`}
                          >
                            YES
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              setResolutionDraft(market.marketId, { outcome: false })
                            }
                            className={`rounded-xl border px-4 py-2 text-sm ${
                              !draft.outcome
                                ? "border-rose-500 bg-rose-500/10 text-rose-600"
                                : "text-muted-foreground"
                            }`}
                          >
                            NO
                          </button>
                        </div>

                        <div>
                          <label className="mb-2 block text-sm font-medium">
                            Evidence URL
                          </label>
                          <input
                            type="text"
                            value={draft.evidenceUrl}
                            onChange={(e) =>
                              setResolutionDraft(market.marketId, {
                                evidenceUrl: e.target.value,
                              })
                            }
                            placeholder="https://..."
                            className="w-full rounded-xl border bg-background px-4 py-3 text-sm outline-none transition focus:border-primary"
                          />
                        </div>

                        <div>
                          <label className="mb-2 block text-sm font-medium">
                            Resolution notes
                          </label>
                          <textarea
                            value={draft.notes}
                            onChange={(e) =>
                              setResolutionDraft(market.marketId, {
                                notes: e.target.value,
                              })
                            }
                            placeholder="Explain briefly why this outcome was selected."
                            rows={4}
                            className="w-full rounded-xl border bg-background px-4 py-3 text-sm outline-none transition focus:border-primary"
                          />
                        </div>

                        <button
                          type="button"
                          onClick={() => handleResolveMarket(market.marketId)}
                          disabled={!isConnected || isWritePending || isConfirming}
                          className="rounded-xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Resolve Market
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
